import { css } from "@emotion/react";
import React, { useState } from "react";

import { Info } from "@livechat/design-system-icons";
import { Icon, SegmentedControl, Switch, Text, Tooltip } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { IAppearance } from "store/entities/application-settings/interfaces";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import EditTemplate from "./EditTemplate";
import PreviewTemplate from "./PreviewTemplate";
import { TestEmailTemplate } from "./components/TestEmailTemplate";
import { ContentType, TabMode } from "./constants";

export interface ITemplateEditorProps {
  sendTest: (emails: string[]) => void;
  templates: Record<ContentType, string>;
  disabled: boolean;
  onContentChange: (contentType: ContentType, content: string) => void;
  hasChanged: boolean;
  isVisualTemplate: boolean;
  appearance: IAppearance;
}

const wrapperStyles = css`
  font-size: 14px;
  line-height: 1.4;
`;

const TemplateComposerContent: React.FC<ITemplateEditorProps> = ({
  disabled,
  sendTest,
  templates,
  onContentChange,
  hasChanged,
  isVisualTemplate,
  appearance,
}) => {
  const [screenIndex, setScreenIndex] = useState(ContentType.HTML);
  const [mode, setMode] = useState<TabMode>(TabMode.Edit);
  const isBannerVisible = Boolean(document.querySelector('[data-testid="notification-banner"]'));

  const handleClick = (index: ContentType.HTML, event: React.MouseEvent): void => {
    event.preventDefault();
    setScreenIndex(index);

    if (index === ContentType.HTML) {
      trackEngagement("HTML template editor: show HTML", EventPlace.Settings);
    } else {
      trackEngagement("HTML template editor: Show plain text", EventPlace.Settings);
    }
  };

  const handleEditorModeChange = (_: React.FormEvent, checked: boolean): void => {
    if (checked) {
      setMode(TabMode.Preview);
      trackEngagement("HTML template editor: Show preview toggled", EventPlace.Settings, { toMode: TabMode.Preview });
    } else {
      setMode(TabMode.Edit);
      trackEngagement("HTML template editor: Show preview toggled", EventPlace.Settings, { toMode: TabMode.Edit });
    }
  };

  return (
    <Block paddingV={!isVisualTemplate ? "1em" : undefined} width="100%">
      <Flex column css={wrapperStyles}>
        {!isVisualTemplate && (
          <Flex alignItems="center" justifyContent="space-between">
            <Flex alignItems="center">
              <SegmentedControl
                size="compact"
                currentId={screenIndex}
                // @ts-expect-error TS(2322)
                onButtonClick={handleClick}
                css={css({ marginRight: "8px" })}
                buttons={[
                  {
                    label: "HTML",
                    id: ContentType.HTML,
                  },
                  {
                    label: "Plain text",
                    id: ContentType.PlainText,
                  },
                ]}
              />
              <Tooltip placement="top" triggerRenderer={() => <Icon source={Info} />} kind="invert">
                <Text>
                  <strong>HTML</strong> - the HTML email template can have color, style, images, and other multimedia.
                  <br />
                  <br />
                  <strong>Plain text</strong> - the plain text email template contains only text. There are no images,
                  stylized fonts, or hyperlinks.
                </Text>
              </Tooltip>
              <Block marginLeft="24px" marginRight="10px">
                <Text>Preview</Text>
              </Block>
              <Switch
                onChange={handleEditorModeChange}
                size="medium"
                on={mode === TabMode.Preview}
                name="Show preview"
              />
            </Flex>
            <TestEmailTemplate hasChanged={hasChanged} sendTest={sendTest} disabled={disabled} />
          </Flex>
        )}
        {mode === TabMode.Edit && !isVisualTemplate && (
          <EditTemplate
            contentType={screenIndex}
            disabled={disabled}
            templates={templates}
            onContentChange={onContentChange}
            isBannerVisible={isBannerVisible}
          />
        )}
        {(isVisualTemplate || mode === TabMode.Preview) && (
          <PreviewTemplate
            contentType={screenIndex}
            templates={templates}
            isBannerVisible={isBannerVisible}
            appearance={appearance}
            hasChanged={hasChanged}
            sendTest={sendTest}
            disabled={disabled}
            isVisualBuilderPreview={isVisualTemplate}
          />
        )}
      </Flex>
    </Block>
  );
};

export default TemplateComposerContent;
